/**
 * Chat topic icons (spike S-324): Phosphor Duotone, the only Phosphor in Crew;
 * everything else is Lucide (nav-icons.tsx). Keyed by the names in apps/app's
 * `worker/domain/chat-icons.ts`, which Clef picks from; a test there keeps the
 * two lists the same.
 */
import {
  AirplaneIcon,
  BabyCarriageIcon,
  BabyIcon,
  BackpackIcon,
  BankIcon,
  BarbellIcon,
  BasketballIcon,
  BedIcon,
  BicycleIcon,
  BirdIcon,
  BoneIcon,
  BookOpenIcon,
  BowlFoodIcon,
  BrainIcon,
  BreadIcon,
  BroomIcon,
  BusIcon,
  CakeIcon,
  CalculatorIcon,
  CalendarIcon,
  CarIcon,
  CarrotIcon,
  CatIcon,
  ChalkboardTeacherIcon,
  ChampagneIcon,
  ChartLineUpIcon,
  ChatCircleIcon,
  ClockIcon,
  CoffeeIcon,
  CoinsIcon,
  ConfettiIcon,
  CookingPotIcon,
  CouchIcon,
  CreditCardIcon,
  DogIcon,
  EnvelopeSimpleIcon,
  ExamIcon,
  EyeglassesIcon,
  FileTextIcon,
  FirstAidKitIcon,
  FishIcon,
  FolderIcon,
  ForkKnifeIcon,
  GiftIcon,
  GlobeIcon,
  GraduationCapIcon,
  HandCoinsIcon,
  HandHeartIcon,
  HeartIcon,
  HeartbeatIcon,
  HorseIcon,
  HouseIcon,
  IdentificationCardIcon,
  IslandIcon,
  KeyIcon,
  LegoSmileyIcon,
  LightbulbIcon,
  ListChecksIcon,
  MagnifyingGlassIcon,
  MapTrifoldIcon,
  MoonStarsIcon,
  MountainsIcon,
  MusicNotesIcon,
  PackageIcon,
  PaintRollerIcon,
  PaletteIcon,
  PawPrintIcon,
  PencilIcon,
  PersonSimpleRunIcon,
  PersonSimpleSwimIcon,
  PhoneIcon,
  PiggyBankIcon,
  PillIcon,
  PizzaIcon,
  PlantIcon,
  PuzzlePieceIcon,
  QuestionIcon,
  ReceiptIcon,
  ScalesIcon,
  ShieldCheckIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  SignatureIcon,
  SneakerIcon,
  SoccerBallIcon,
  SparkleIcon,
  StethoscopeIcon,
  StorefrontIcon,
  SuitcaseRollingIcon,
  SyringeIcon,
  TShirtIcon,
  TagIcon,
  TennisBallIcon,
  TentIcon,
  TicketIcon,
  ToothIcon,
  TrainIcon,
  UsersThreeIcon,
  WalletIcon,
  WashingMachineIcon,
  WineIcon,
  WrenchIcon,
  type Icon,
} from "@phosphor-icons/react";
import { cn } from "../lib/utils";

export const CHAT_ICON_COMPONENTS: Record<string, Icon> = {
  "cooking-pot": CookingPotIcon,
  "bowl-food": BowlFoodIcon,
  "fork-knife": ForkKnifeIcon,
  pizza: PizzaIcon,
  bread: BreadIcon,
  carrot: CarrotIcon,
  coffee: CoffeeIcon,
  wine: WineIcon,
  backpack: BackpackIcon,
  "book-open": BookOpenIcon,
  pencil: PencilIcon,
  calculator: CalculatorIcon,
  exam: ExamIcon,
  "chalkboard-teacher": ChalkboardTeacherIcon,
  "graduation-cap": GraduationCapIcon,
  bus: BusIcon,
  baby: BabyIcon,
  "baby-carriage": BabyCarriageIcon,
  "hand-heart": HandHeartIcon,
  "moon-stars": MoonStarsIcon,
  "lego-smiley": LegoSmileyIcon,
  "puzzle-piece": PuzzlePieceIcon,
  palette: PaletteIcon,
  "music-notes": MusicNotesIcon,
  stethoscope: StethoscopeIcon,
  pill: PillIcon,
  "first-aid-kit": FirstAidKitIcon,
  syringe: SyringeIcon,
  tooth: ToothIcon,
  eyeglasses: EyeglassesIcon,
  brain: BrainIcon,
  heartbeat: HeartbeatIcon,
  wallet: WalletIcon,
  receipt: ReceiptIcon,
  "credit-card": CreditCardIcon,
  bank: BankIcon,
  "piggy-bank": PiggyBankIcon,
  "chart-line-up": ChartLineUpIcon,
  coins: CoinsIcon,
  "hand-coins": HandCoinsIcon,
  house: HouseIcon,
  wrench: WrenchIcon,
  "paint-roller": PaintRollerIcon,
  broom: BroomIcon,
  "washing-machine": WashingMachineIcon,
  couch: CouchIcon,
  plant: PlantIcon,
  key: KeyIcon,
  airplane: AirplaneIcon,
  "suitcase-rolling": SuitcaseRollingIcon,
  island: IslandIcon,
  bed: BedIcon,
  car: CarIcon,
  train: TrainIcon,
  tent: TentIcon,
  "map-trifold": MapTrifoldIcon,
  "shopping-cart": ShoppingCartIcon,
  "shopping-bag": ShoppingBagIcon,
  "t-shirt": TShirtIcon,
  sneaker: SneakerIcon,
  package: PackageIcon,
  tag: TagIcon,
  storefront: StorefrontIcon,
  calendar: CalendarIcon,
  cake: CakeIcon,
  confetti: ConfettiIcon,
  gift: GiftIcon,
  heart: HeartIcon,
  ticket: TicketIcon,
  "users-three": UsersThreeIcon,
  champagne: ChampagneIcon,
  "file-text": FileTextIcon,
  folder: FolderIcon,
  "envelope-simple": EnvelopeSimpleIcon,
  phone: PhoneIcon,
  "identification-card": IdentificationCardIcon,
  signature: SignatureIcon,
  "shield-check": ShieldCheckIcon,
  scales: ScalesIcon,
  dog: DogIcon,
  cat: CatIcon,
  "paw-print": PawPrintIcon,
  bone: BoneIcon,
  fish: FishIcon,
  bird: BirdIcon,
  horse: HorseIcon,
  barbell: BarbellIcon,
  "person-simple-run": PersonSimpleRunIcon,
  bicycle: BicycleIcon,
  "person-simple-swim": PersonSimpleSwimIcon,
  "soccer-ball": SoccerBallIcon,
  basketball: BasketballIcon,
  "tennis-ball": TennisBallIcon,
  mountains: MountainsIcon,
  "chat-circle": ChatCircleIcon,
  "list-checks": ListChecksIcon,
  clock: ClockIcon,
  lightbulb: LightbulbIcon,
  "magnifying-glass": MagnifyingGlassIcon,
  globe: GlobeIcon,
  question: QuestionIcon,
  sparkle: SparkleIcon,
};

/**
 * A chat's topic icon in Phosphor Duotone, its second tone at 30% (Phosphor
 * fixes it at 20%, which fades on the tile tints: S-324 §4). Null for a name
 * this build doesn't know, so the tile keeps its letter.
 */
export function ChatIcon({
  name,
  size = 19,
  className,
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const Glyph = CHAT_ICON_COMPONENTS[name];
  if (!Glyph) return null;
  return (
    <Glyph
      aria-hidden
      data-chat-icon={name}
      size={size}
      weight="duotone"
      className={cn("shrink-0 [&_[opacity]]:opacity-30", className)}
    />
  );
}
